import React from 'react';
import { useQuery } from '@apollo/client';
import { Button, Col, Label, Row } from 'reactstrap';
import { DOWNLOAD_INVOICE } from 'pages/Invoices/InvoicesGraphQL';
import CardWithTitle from 'components/CardWithTitle';
import Base64ToPdf from 'pages/Invoices/components/PDFViewer';
import Loader from 'components/common/Loader';
import moment from 'moment';
import { INVOICE_STATUS } from 'utils/constants';
import { toast } from 'react-toastify';
import { authContext } from 'contexts/AuthContext';
import { axiosGetAPI } from 'services';
import { downloadPdfFile, sanitizePdfFileName } from 'utils/pdfDownload';

const getReadableError = (apiError, fallbackMessage) => {
  const graphQlMessage = apiError?.graphQLErrors?.[0]?.message;
  const networkMessage = apiError?.networkError?.result?.errors?.[0]?.message;
  const message = graphQlMessage || networkMessage || apiError?.message;
  return message || fallbackMessage;
};

const extractFirstInvoiceDocument = (invoice) => {
  if (!invoice) return '';

  const candidateValues = [
    invoice?.invoiceFiles,
    invoice?.invoiceFile,
    invoice?.invoiceUrl,
    invoice?.document,
    invoice?.documents,
  ];

  for (let index = 0; index < candidateValues.length; index += 1) {
    const candidate = candidateValues[index];
    if (candidate) {
      if (Array.isArray(candidate)) {
        const [firstValue] = candidate;
        if (typeof firstValue === 'string') return firstValue;
        if (firstValue?.url) return firstValue.url;
      }

      if (typeof candidate === 'string') {
        const trimmedCandidate = candidate.trim();
        if (trimmedCandidate) {
          try {
            const parsedCandidate = JSON.parse(trimmedCandidate);
            if (Array.isArray(parsedCandidate)) {
              const [firstValue] = parsedCandidate;
              if (typeof firstValue === 'string') return firstValue;
              if (firstValue?.url) return firstValue.url;
            } else if (parsedCandidate?.url) {
              return parsedCandidate.url;
            }
          } catch {
            return trimmedCandidate;
          }
        }
      }

      if (candidate?.url) return candidate.url;
    }
  }

  return '';
};

const PaymentDetails = ({ invoicesDetail }) => {
  const { auth } = React.useContext(authContext);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const { data, loading, error, refetch } = useQuery(DOWNLOAD_INVOICE, {
    variables: { invoiceId: invoicesDetail?.id },
    fetchPolicy: 'network-only',
    skip: !invoicesDetail?.id,
  });

  const showPdf = () => {
    if (!invoicesDetail?.id) {
      return 'Invoice ID is not available.';
    }
    if (loading) {
      return <Loader />;
    }
    if (error) {
      return getReadableError(
        error,
        'Error while getting your pdf. Please refresh the page or try again later',
      );
    }
    if (data?.downloadInvoice) {
      return <Base64ToPdf url={data?.downloadInvoice} />;
    }
    return 'Invoice document is not available.';
  };

  const getDocumentFromRestInvoice = async () => {
    if (!invoicesDetail?.order?.id) return '';

    const response = await axiosGetAPI(
      `${process.env.REACT_APP_REST_API_ENDPOINT}invoice/${invoicesDetail?.order?.id}`,
      auth,
    );

    if (!response?.isSuccessful) return '';

    const matchedInvoice = response?.data?.data?.find(
      (invoice) => String(invoice?.id) === String(invoicesDetail?.id),
    );
    return extractFirstInvoiceDocument(matchedInvoice);
  };

  const handleDownload = async () => {
    if (!invoicesDetail?.id || isDownloading) {
      return;
    }

    setIsDownloading(true);

    try {
      let invoiceSource = data?.downloadInvoice;
      if (!invoiceSource) {
        const refetchedInvoice = await refetch({
          invoiceId: invoicesDetail?.id,
        });
        invoiceSource = refetchedInvoice?.data?.downloadInvoice;
      }
      if (!invoiceSource) {
        invoiceSource = await getDocumentFromRestInvoice();
      }
      if (!invoiceSource) {
        toast.error('Invoice document is not available for download.');
        return;
      }

      const filename = sanitizePdfFileName(
        `Invoice_${
          invoicesDetail?.order?.buyer?.tradeName
        }_${moment(invoicesDetail?.createdAt).format('D MMM, YYYY')}.pdf`,
      );

      await downloadPdfFile(invoiceSource, filename, {
        authToken: auth?.data?.token,
      });
    } catch (downloadError) {
      toast.error(
        getReadableError(
          downloadError,
          'Failed to download invoice document. Please try again.',
        ),
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div>
      {' '}
      <CardWithTitle title={invoicesDetail?.name}>
        <div className="product-info-row mb-2 d-flex align-items-start align-items-lg-center position-relative">
          {/* <img src={productImg} alt="product-img" /> */}
          <Row className="mb-2 w-100 align-items-center">
            <Col xs={5} sm={3} md={3} lg={3}>
              <Label className="f-14 fw-medium text-gray  d-block mb-0">
                Order ID
              </Label>
              <p className="f-14 mb-0">{invoicesDetail?.order?.id}</p>
            </Col>
            <Col xs={5} sm={3} md={3} lg={3}>
              <Label className="f-14 fw-medium text-gray d-block mb-0">
                Status
              </Label>
              <p className="f-14 mb-0 text-capitalize">
                {INVOICE_STATUS?.[invoicesDetail?.invoiceStatus]}
              </p>
            </Col>
            <Col xs={5} sm={3} md={3} lg={3}>
              <Label className="f-14 fw-medium text-gray d-block mb-0">
                Created on
              </Label>
              <p className="f-14 mb-0 text-capitalize">
                {moment(invoicesDetail?.createdAt).format('D MMM, YYYY')}
              </p>
            </Col>
            <Col xs={8} sm={5} md={4} lg={3}>
              <Label className="f-14 fw-medium text-gray d-block mb-0">
                Documents
              </Label>
              <Button
                color="link"
                className="p-0 f-14 text-decoration-none text-start"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {`${invoicesDetail?.order?.buyer?.tradeName}.pdf`}
              </Button>
            </Col>
          </Row>
        </div>
      </CardWithTitle>
      {/* <BuyerPaymentDetailsTable invoicesDetail={invoicesDetail} />
      <SellerPaymentDetailsTable invoicesDetail={invoicesDetail} /> */}
      <CardWithTitle title="">{showPdf()}</CardWithTitle>
    </div>
  );
};

export default PaymentDetails;
